import type {
  OrganizationRankingSummaryResponse,
  PlayerRatingChange,
} from '@on3/api';
import { Link } from '@on3/ui-lib/components/Link';
import { useSite } from '@on3/ui-lib/contexts/SiteProvider';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { ordinalSuffixOf } from '@on3/ui-lib/utils/ordinalSuffixOf';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { useCallback } from 'react';

import styles from './TeamRanking.module.scss';

interface ITeamRankingProps {
  teamRank: OrganizationRankingSummaryResponse;
  statusCounts?: { commits: number; transfers: number };
}

export const TeamRanking = ({ teamRank, statusCounts }: ITeamRankingProps) => {
  const {
    conference,
    year,
    classRatingCurrent,
    classRatingPrevious,
    classRatingChange,
    nationalRankCurrent,
    nationalRankPrevious,
    nationalRankChange,
    conferenceConsensusRankChange,
    conferenceConsensusRankCurrent,
    conferenceConsensusRankPrevious,
    inState,
    averageNilValue,
    avgDistance,
    blueChips,
    headCoach,
    directorPersonnel,
  } = teamRank;
  const { sportsData } = useSite();
  const { routeParams } = useDynamicRoute();
  const { commits, transfers } = statusCounts || {};
  const isIndustrySport = sportsData?.find((sportData) => {
    return slugify(sportData.name) === routeParams.sport;
  })?.isIndustryRankable;

  const changeString = useCallback(
    (rankChange: Record<string, PlayerRatingChange> | undefined) => {
      if (!rankChange) return;

      return Object.values(rankChange)[0];
    },
    [],
  );
  const calculateChange = useCallback(
    (num1?: number | null, num2?: number | null) => {
      if (!num1 || !num2) return;

      const difference = num1 - num2;

      return difference;
    },
    [],
  );

  const formatRank = useCallback((rank?: number | null) => {
    if (!rank) return '-';

    return ordinalSuffixOf(rank);
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.block}>
        {isIndustrySport && (
          <ul className={styles.teamRankings}>
            <li>
              <h3>National Rank</h3>
              <div className={styles.container} data-name="national">
                <Link
                  className={styles.columnOne}
                  href={`/rivals/rankings/industry-team/${routeParams.sport}/${routeParams.year}/`}
                >
                  <dl>
                    <dt>Year</dt>
                    <dd data-name="year">{year}</dd>
                  </dl>
                  <div className={styles.rankPlusChange}>
                    <dl>
                      <dt>Current Rank</dt>
                      <dd data-name="national-rank">
                        {formatRank(nationalRankCurrent)}
                      </dd>
                    </dl>
                    <dl>
                      <dt>Change in Rank</dt>
                      <dd
                        data-name="change"
                        data-type={changeString(nationalRankChange)}
                      >
                        {calculateChange(
                          nationalRankPrevious,
                          nationalRankCurrent,
                        )}
                      </dd>
                    </dl>
                  </div>
                </Link>
                <Link
                  className={styles.columnTwo}
                  href={`/rivals/rankings/industry-team/${routeParams.sport}/${
                    +routeParams.year - 1
                  }/`}
                >
                  <dl>
                    <dt aria-label="Previous Rank">Prev.</dt>
                    <dd>{formatRank(nationalRankPrevious)}</dd>
                  </dl>
                </Link>
              </div>
            </li>
            <li>
              <h3>{conference}</h3>
              <div className={styles.container} data-name="conference">
                <Link
                  className={styles.columnOne}
                  href={`/rivals/rankings/industry-team/${routeParams.sport}/${
                    routeParams.year
                  }?conference=${slugify(conference)}`}
                >
                  <dl>
                    <dt>Year</dt>
                    <dd data-name="year">{year}</dd>
                  </dl>
                  <div className={styles.rankPlusChange}>
                    <dl>
                      <dt>Current Rank</dt>
                      <dd>{formatRank(conferenceConsensusRankCurrent)}</dd>
                    </dl>
                    <dl>
                      <dt>Change in Rank</dt>
                      <dd
                        data-name="change"
                        data-type={changeString(conferenceConsensusRankChange)}
                      >
                        {calculateChange(
                          conferenceConsensusRankPrevious,
                          conferenceConsensusRankCurrent,
                        )}
                      </dd>
                    </dl>
                  </div>
                </Link>
                <Link
                  className={styles.columnTwo}
                  href={`/rivals/rankings/industry-team/${routeParams.sport}/${
                    +routeParams.year - 1
                  }?conference=${slugify(conference)}`}
                >
                  <dl>
                    <dt aria-label="Previous Rank">Prev.</dt>
                    <dd>{formatRank(conferenceConsensusRankPrevious)}</dd>
                  </dl>
                </Link>
              </div>
            </li>
            <li>
              <h3>Class</h3>
              <div className={styles.container} data-name="class">
                <Link
                  className={styles.columnOne}
                  href={`/rivals/rankings/industry-team/${routeParams.sport}/${routeParams.year}/`}
                >
                  <dl>
                    <dt>Year</dt>
                    <dd data-name="year">{year}</dd>
                  </dl>
                  <div className={styles.rankPlusChange}>
                    <dl>
                      <dt>Current Rating</dt>
                      <dd>{classRatingCurrent?.toFixed(2)}</dd>
                    </dl>
                    <dl>
                      <dt>Change in Rating</dt>
                      <dd
                        data-name="change"
                        data-type={changeString(classRatingChange)}
                      >
                        {calculateChange(
                          classRatingCurrent,
                          classRatingPrevious,
                        )?.toFixed(2)}
                      </dd>
                    </dl>
                  </div>
                </Link>
                <Link
                  className={styles.columnTwo}
                  href={`/rivals/rankings/industry-team/${routeParams.sport}/${
                    +routeParams.year - 1
                  }/`}
                >
                  <dl>
                    <dt aria-label="Previous Rating">Prev.</dt>
                    <dd>{classRatingPrevious?.toFixed(2)}</dd>
                  </dl>
                </Link>
              </div>
            </li>
          </ul>
        )}
        <ul className={styles.details}>
          {typeof inState === 'number' && (
            <li>
              <dl>
                <dt>In-state:</dt>
                <dd>{Math.round(inState)}%</dd>
              </dl>
            </li>
          )}
          {typeof avgDistance === 'number' && (
            <li>
              <dl>
                <dt aria-label="Average Distance">Avg. Distance:</dt>
                <dd>
                  {avgDistance?.toFixed(1)} <span aria-label="miles">mi.</span>
                </dd>
              </dl>
            </li>
          )}

          {typeof averageNilValue === 'number' && (
            <li>
              <dl>
                <dt aria-label="Average NIL Value">Avg. NIL Value:</dt>
                <dd>{formatCurrency(averageNilValue)}</dd>
              </dl>
            </li>
          )}

          {typeof blueChips === 'number' && (
            <li>
              <dl>
                <dt>Blue Chips:</dt>
                <dd>{Math.round(blueChips)}%</dd>
              </dl>
            </li>
          )}

          {headCoach && (
            <li>
              <dl>
                <dt>Head Coach:</dt>
                <dd>
                  <Link href={`/rivals/coach/${headCoach?.slug}/`} useSiteColor>
                    {headCoach?.name}
                  </Link>
                </dd>
              </dl>
            </li>
          )}

          {directorPersonnel && (
            <li>
              <dl>
                <dt>Dir. of Player Personnel:</dt>
                <dd>
                  <Link
                    href={`/rivals/coach/${directorPersonnel.slug}/`}
                    useSiteColor
                  >
                    {directorPersonnel?.name}
                  </Link>
                </dd>
              </dl>
            </li>
          )}
          {typeof commits === 'number' && (
            <li>
              <dl>
                <dt>Commits:</dt>
                <dd>{commits}</dd>
              </dl>
            </li>
          )}
          {typeof transfers === 'number' && (
            <li>
              <dl>
                <dt>Transfers:</dt>
                <dd>{transfers}</dd>
              </dl>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};
